import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { QK } from '../../lib/queryKeys';
import SidebarLayout from '../../components/layout/SidebarLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { ArrowLeft, Download, Mail, Globe, Trash2, Undo2, Loader2 } from 'lucide-react';
import EmailSendHistory from '../../components/shared/EmailSendHistory';
import { toast } from 'sonner';
import api from '../../services/api';
import StatementEmailDialog from '../../components/invoices/StatementEmailDialog';
import { calcInvoiceAmount } from '../../utils/invoiceCalc';

const fmt = (amount) => {
  if (!amount && amount !== 0) return '-';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(amount);
};

const paymentBadge = {
  paid: 'bg-green-100 text-green-700',
  unpaid: 'bg-yellow-100 text-yellow-700',
};

const GREEN_DOOR_LOGO = 'https://customer-assets.emergentagent.com/job_thoroughbred-deals/artifacts/xmrjz10b_Powered%20By%20Green%20Door.png';

const StatementDetailPage = () => {
  const navigate = useNavigate();
  const { statementId } = useParams();
  const qc = useQueryClient();

  const { data: stmt, isLoading: loading } = useQuery({
    queryKey: QK.statement(statementId),
    queryFn: () => api.get(`/invoices/statements/${statementId}`).then(r => r.data),
    enabled: !!statementId,
    onError: () => toast.error('Failed to load statement'),
  });
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: QK.statement(statementId) });
    qc.invalidateQueries({ queryKey: QK.statements() });
  };

  const [downloading, setDownloading] = useState(false);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);

  const handleDownloadPdf = async () => {
    setDownloading(true);
    try {
      const res = await api.post('/invoices/generate-statement-pdf', {
        crm_entity_id: stmt.crm_entity_id,
        invoice_ids: stmt.invoice_ids || [],
      }, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Statement-${stmt.crm_entity_name || 'owner'}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => window.URL.revokeObjectURL(url), 100);
    } catch { toast.error('Failed to download PDF'); }
    setDownloading(false);
  };

  const handlePublish = async () => {
    try {
      await api.post(`/invoices/statements/${statementId}/publish-to-portal`);
      toast.success('Statement published to portal');
      invalidate();
    } catch { toast.error('Failed to publish'); }
  };

  const handleUnpublish = async () => {
    try {
      await api.post(`/invoices/statements/${statementId}/unpublish-from-portal`);
      toast.success('Statement removed from portal');
      invalidate();
    } catch { toast.error('Failed to unpublish'); }
  };

  const handleDelete = async () => {
    setConfirmAction({
      message: 'Delete this statement?',
      onConfirm: async () => {
        try {
          await api.delete(`/invoices/statements/${statementId}`);
          toast.success('Statement deleted');
          navigate('/invoices/statements');
        } catch { toast.error('Failed to delete'); }
        setConfirmAction(null);
      }
    });
  };

  // Use authoritative calcInvoiceAmount (handles Direct Allocations correctly)
  const calcAmount = (inv) => {
    const { amount, isPayable: payable } = calcInvoiceAmount(inv);
    return payable ? amount : -amount;
  };

  const isPayable = (inv) => calcInvoiceAmount(inv).isPayable;

  if (loading) {
    return <SidebarLayout><div className="flex items-center justify-center h-96"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div></SidebarLayout>;
  }

  if (!stmt) {
    return <SidebarLayout><div className="text-center py-20 text-muted-foreground">Statement not found</div></SidebarLayout>;
  }

  const invoices = stmt.invoices || [];
  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : '';

  // Recalculate totals live from the invoice amounts so the footer always
  // matches the displayed line items — even for statements generated before
  // the is_proceeds fix in _calc_invoice_net was deployed.
  const liveTotals = invoices.reduce((acc, inv) => {
    const { amount, isPayable: p } = calcInvoiceAmount(inv);
    const abs = Math.abs(amount);
    return p
      ? { ...acc, proceeds: acc.proceeds + abs }
      : { ...acc, expenses: acc.expenses + abs };
  }, { expenses: 0, proceeds: 0 });
  const liveExpenses   = liveTotals.expenses;
  const liveProceeds   = liveTotals.proceeds;
  const liveNetBalance = liveProceeds - liveExpenses;

  return (
    <SidebarLayout>
      <div className="space-y-4">
        {/* Top Bar */}
        <div className="flex items-center justify-between">
          <Button variant="ghost" size="sm" onClick={() => navigate('/invoices/statements')}>
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Statements
          </Button>
          <Badge className="bg-green-100 text-green-700">Approved</Badge>
        </div>

        {/* Action Bar */}
        <Card>
          <CardContent className="p-3">
            <div className="flex flex-col gap-2">
              {/* Top row: Download + actions not yet taken */}
              <div className="flex items-center gap-2">
                <Button size="sm" variant="outline" onClick={handleDownloadPdf} disabled={downloading}>
                  {downloading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                  Download PDF
                </Button>
                {!stmt.emailed && (
                  <Button size="sm" variant="outline" onClick={() => setEmailDialogOpen(true)}>
                    <Mail className="w-4 h-4 mr-2" /> Email via Green Door
                  </Button>
                )}
                {!stmt.published_to_portal && (
                  <Button size="sm" variant="outline" onClick={handlePublish}><Globe className="w-4 h-4 mr-2" /> Publish to Portal</Button>
                )}
                <div className="ml-auto">
                  <Button variant="ghost" size="sm" className="text-muted-foreground hover:text-red-600" onClick={handleDelete}><Trash2 className="w-4 h-4" /></Button>
                </div>
              </div>
              {/* Email details row */}
              {stmt.emailed && (
                <EmailSendHistory
                  sends={stmt.email_sends && stmt.email_sends.length > 0 ? stmt.email_sends : [{ sent_at: stmt.emailed_at, sent_by: stmt.emailed_by, sent_to: stmt.emailed_to, eml_file_id: stmt.emailed_eml_file_id }]}
                  onResend={() => setEmailDialogOpen(true)}
                  emlFilename="statement_email.eml"
                />
              )}
              {/* Publish details row */}
              {stmt.published_to_portal && (
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-2 text-sm border rounded-md px-3 py-1.5 bg-blue-50 border-blue-200">
                    <Globe className="w-4 h-4 text-blue-600 flex-shrink-0" />
                    <span className="text-blue-700 font-medium">
                      Published to Portal on {fmtDate(stmt.published_to_portal_at)}{stmt.published_to_portal_by ? ` by ${stmt.published_to_portal_by}` : ''}
                    </span>
                  </div>
                  <Button size="sm" variant="ghost" className="text-muted-foreground hover:text-red-600" onClick={handleUnpublish}><Undo2 className="w-4 h-4 mr-1" /> Undo Publish</Button>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Statement Document */}
        <Card>
          <CardContent className="p-8 space-y-8">
            {/* Header */}
            <div className="flex justify-between items-start">
              <div>
                {stmt.tenant_logo_url && <img src={stmt.tenant_logo_url} alt="Logo" className="h-16 mb-2 object-contain" />}
                {!stmt.tenant_logo_url && <p className="font-serif text-2xl font-bold">{stmt.tenant_name}</p>}
              </div>
              <div className="text-right">
                <p className="font-serif text-3xl font-bold text-muted-foreground mb-2">STATEMENT</p>
                <p className="text-sm font-medium">{stmt.tenant_name}</p>
              </div>
            </div>

            {/* Statement Details */}
            <div className="flex justify-between border-t border-b py-4">
              <div>
                <p className="text-xs text-muted-foreground font-semibold mb-1">STATEMENT FOR</p>
                <p className="text-sm font-semibold">{stmt.crm_entity_name}</p>
              </div>
              <div className="text-right text-sm space-y-1">
                <div><span className="text-muted-foreground">Date: </span><span className="font-medium">{stmt.generated_at ? new Date(stmt.generated_at).toLocaleDateString() : '-'}</span></div>
                <div><span className="text-muted-foreground">Invoices: </span><span className="font-medium">{stmt.invoice_count}</span></div>
                {stmt.horse_names?.length > 0 && (
                  <div><span className="text-muted-foreground">Horses: </span><span className="font-medium">{stmt.horse_names.join(', ')}</span></div>
                )}
              </div>
            </div>

            {/* Invoice Table */}
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Invoice #</TableHead>
                  <TableHead>Horse</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Payment</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {invoices.map(inv => {
                  const amount = calcAmount(inv);
                  const payable = isPayable(inv);
                  return (
                    <TableRow key={inv.id} className="cursor-pointer hover:bg-muted/30" onClick={() => navigate(`/invoices/${inv.id}`)}>
                      <TableCell className="font-medium">{inv.invoice_number_formatted}</TableCell>
                      <TableCell>{inv.horse_name || '-'}</TableCell>
                      <TableCell className="text-sm">{inv.invoice_date ? new Date(inv.invoice_date).toLocaleDateString() : '-'}</TableCell>
                      <TableCell><Badge className={paymentBadge[inv.payment_status] || 'bg-gray-100'}>{inv.payment_status?.replace(/\b\w/g, c => c.toUpperCase())}</Badge></TableCell>
                      <TableCell className={`text-right font-medium ${payable ? 'text-primary' : ''}`}>
                        {payable ? `(${fmt(Math.abs(amount))})` : fmt(Math.abs(amount))}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>

            {/* Totals */}
            <div className="border-t pt-4 space-y-2 text-sm">
              <div className="flex justify-end gap-8"><span className="text-muted-foreground">Total Expenses</span><span className="font-medium w-32 text-right">{fmt(liveExpenses)}</span></div>
              <div className="flex justify-end gap-8"><span className="text-muted-foreground">Total Proceeds</span><span className="font-medium w-32 text-right text-primary">{fmt(liveProceeds)}</span></div>
              <div className="flex justify-end gap-8 text-base font-bold border-t pt-2">
                <span>Net Balance</span>
                <span className={`w-32 text-right ${liveNetBalance >= 0 ? 'text-primary' : 'text-red-600'}`}>
                  {fmt(Math.abs(liveNetBalance))}
                </span>
              </div>
              <div className="flex justify-end">
                <span className="text-xs text-muted-foreground w-32 text-right">{liveNetBalance >= 0 ? 'Payable to owner' : 'Due from owner'}</span>
              </div>
            </div>

            {/* Powered by */}
            <div className="pt-8 flex justify-center">
              <img src={GREEN_DOOR_LOGO} alt="Powered by Green Door" className="w-1/4 mx-auto opacity-60" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Email Dialog */}
      <StatementEmailDialog
        open={emailDialogOpen}
        onClose={(sent) => {
          setEmailDialogOpen(false);
          if (sent) invalidate();
        }}
        statementId={statementId}
        entityName={stmt?.crm_entity_name}
      />

      {/* Confirmation Dialog */}
      <Dialog open={!!confirmAction} onOpenChange={(open) => { if (!open) setConfirmAction(null); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Confirm</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground py-2">{confirmAction?.message}</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmAction(null)}>Cancel</Button>
            <Button variant="destructive" onClick={confirmAction?.onConfirm}>Confirm</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </SidebarLayout>
  );
};

export default StatementDetailPage;
